import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { cemeteryAPI, backupAPI, importAPI } from '../services/api';
import './BackupPage.css';
import Toast from '../components/Toast';
import CemeteryStatsGrid from '../components/CemeteryStatsGrid';
import CemeteryContextBar from '../components/CemeteryContextBar';

function BackupPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [cemeteries, setCemeteries] = useState([]);
  const [selectedCemetery, setSelectedCemetery] = useState(searchParams.get('cemetery') || '');
  const [searchQuery, setSearchQuery] = useState('');
  const [backupTypes, setBackupTypes] = useState({
    plots: true,
    persons: true,
    interments: true,
    applications: true,
    stories: true,
    events: true,
    notes: true,
    geojson: true,
  });
  const [excludeUniqueId, setExcludeUniqueId] = useState(false);
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [taskId, setTaskId] = useState(null);
  const [backupStatus, setBackupStatus] = useState(null);

  const [cemeteryStats, setCemeteryStats] = useState(null);

  useEffect(() => {
    loadCemeteries();
  }, []);

  useEffect(() => {
    if (selectedCemetery) {
      loadBackups();
      loadCemeteryStats();
    } else {
      setCemeteryStats(null);
    }
  }, [selectedCemetery]);

  useEffect(() => {
    // Poll backup status if task is running
    if (taskId) {
      const interval = setInterval(() => {
        checkBackupStatus();
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [taskId]);

  const loadCemeteries = async () => {
    try {
      const response = await cemeteryAPI.list();
      setCemeteries(response.data.cemeteries || response.data);
    } catch (err) {
      console.error('Failed to load cemeteries:', err);
    }
  };

  const filteredCemeteries = cemeteries.filter(cemetery =>
    cemetery.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    cemetery.unique_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSelectCemetery = (cemeteryUniqueName) => {
    setSelectedCemetery(cemeteryUniqueName);
    setSearchQuery('');
  };

  const loadBackups = async () => {
    try {
      const response = await backupAPI.getHistory({
        cemetery_name: selectedCemetery,
        page_size: 50,
      });
      setBackups(response.data.backups || []);
    } catch (err) {
      console.error('Failed to load backups:', err);
    }
  };

  const loadCemeteryStats = async ({ refresh = false } = {}) => {
    try {
      const response = await importAPI.getStatistics(selectedCemetery, { refresh });
      setCemeteryStats(response.data || {});
    } catch (err) {
      console.error('Failed to load cemetery stats:', err);
      setCemeteryStats({});
    }
  };

  const checkBackupStatus = async () => {
    if (!taskId) return;

    try {
      // Use the download endpoint to check status
      const response = await backupAPI.getDownloadStatus(taskId);
      setBackupStatus(response.data);

      // Stop polling if backup is complete or failed
      if (['ready', 'failed', 'not_found'].includes(response.data.status)) {
        setTaskId(null);
        if (response.data.status === 'ready') {
          setMessage({
            type: 'success',
            text: 'Backup completed successfully!',
          });
          loadBackups();
        } else if (response.data.status === 'failed') {
          setMessage({
            type: 'error',
            text: `Backup failed: ${response.data.error || 'Unknown error'}`,
          });
        }
      }
    } catch (err) {
      console.error('Failed to check backup status:', err);
    }
  };

  const handleCancelBackup = async () => {
    if (!window.confirm('Are you sure you want to cancel this backup?')) {
      return;
    }

    try {
      await backupAPI.cancel(taskId);
      setMessage({ type: 'success', text: 'Backup cancelled successfully' });
      setTaskId(null);
      setBackupStatus(null);
      loadBackups();
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.response?.data?.detail || 'Failed to cancel backup',
      });
    }
  };

  const handleBackupTypeChange = (type) => {
    setBackupTypes({
      ...backupTypes,
      [type]: !backupTypes[type],
    });
  };

  const handleCreateBackup = async () => {
    if (!selectedCemetery) {
      setMessage({ type: 'error', text: 'Please select a cemetery' });
      return;
    }

    const selectedTypes = Object.keys(backupTypes).filter((key) => backupTypes[key]);
    if (selectedTypes.length === 0) {
      setMessage({ type: 'error', text: 'Please select at least one backup type' });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const response = await backupAPI.createAsync(selectedCemetery, selectedTypes, excludeUniqueId);
      setTaskId(response.data.task_id);
      setBackupStatus({ status: 'processing', message: 'Backup started...' });
      setMessage({
        type: 'success',
        text: `Backup started successfully! Task ID: ${response.data.task_id}`,
      });
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.response?.data?.detail || 'Failed to create backup',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (backup) => {
    try {
      // If backup has a download_url, use it directly
      if (backup.download_url) {
        window.open(backup.download_url, '_blank');
        return;
      }

      // Otherwise, regenerate the download URL
      const response = await backupAPI.regenerateDownload(backup.id);
      if (response.data.download_url) {
        window.open(response.data.download_url, '_blank');
      } else {
        setMessage({
          type: 'error',
          text: response.data.error || 'Failed to generate download link',
        });
      }
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.response?.data?.detail || 'Failed to download backup',
      });
    }
  };

  const handleCompareToLive = (backup) => {
    navigate(
      `/backup/verify?cemetery=${encodeURIComponent(selectedCemetery)}&backupId=${backup.id}`
    );
  };

  const getStatusColor = (status) => {
    const statusMap = {
      COMPLETED: 'status-completed',
      FAILED: 'status-failed',
      PENDING: 'status-pending',
      PROCESSING: 'status-processing',
      CANCELLED: 'status-cancelled',
    };
    return statusMap[status] || 'status-unknown';
  };

  const handleDelete = async (filename) => {
    if (!window.confirm(`Are you sure you want to delete ${filename}?`)) {
      return;
    }

    try {
      await backupAPI.delete(filename);
      setMessage({ type: 'success', text: 'Backup deleted successfully' });
      loadBackups();
    } catch (err) {
      setMessage({
        type: 'error',
        text: 'Failed to delete backup',
      });
    }
  };

  return (
    <div className="container">
      <CemeteryContextBar
        cemeteryUniqueName={selectedCemetery}
        cemeteryName={cemeteries.find(c => c.unique_name === selectedCemetery)?.name}
        onChange={() => setSelectedCemetery('')}
      />
      <div className="page-header">
        <div className="page-header-text">
          <h1>Backup Management</h1>
          <p>Create and manage cemetery data backups</p>
        </div>
        <div className="page-header-actions">
          <button className="btn btn-primary" onClick={handleCreateBackup} disabled={!selectedCemetery || loading}>
            Create Backup
          </button>
        </div>
      </div>

      <Toast message={message} onDismiss={() => setMessage(null)} />

      <div className="card">
        <h2>Create New Backup</h2>

        <div className="input-group">
          <label htmlFor="cemetery">Select Cemetery</label>
          <div className="search-container">
            <input
              type="text"
              id="cemetery"
              className="form-control"
              placeholder={selectedCemetery ?
                cemeteries.find(c => c.unique_name === selectedCemetery)?.name || selectedCemetery
                : "Search cemeteries..."}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              disabled={loading}
            />
            {searchQuery && filteredCemeteries.length > 0 && (
              <div className="search-results">
                {filteredCemeteries.map((cemetery) => (
                  <div
                    key={cemetery.unique_name}
                    className="search-result-item"
                    onClick={() => handleSelectCemetery(cemetery.unique_name)}
                  >
                    <strong>{cemetery.name}</strong>
                    <p className="text-muted">{cemetery.unique_name}</p>
                  </div>
                ))}
              </div>
            )}
            {searchQuery && filteredCemeteries.length === 0 && (
              <div className="search-results">
                <p className="text-muted">No cemeteries found</p>
              </div>
            )}
          </div>
          {selectedCemetery && (
            <div className="selected-item">
              <strong>Selected:</strong> {cemeteries.find(c => c.unique_name === selectedCemetery)?.name || selectedCemetery}
              <button
                className="btn btn-sm btn-secondary"
                onClick={() => setSelectedCemetery('')}
                disabled={loading}
              >
                Change
              </button>
            </div>
          )}
        </div>

        {selectedCemetery && cemeteryStats && (
          <CemeteryStatsGrid statistics={cemeteryStats} onRefresh={() => loadCemeteryStats({ refresh: true })} />
        )}

        <div className="backup-types">
          <label>Backup Types:</label>
          <div className="checkbox-grid">
            {Object.keys(backupTypes).map((type) => (
              <label key={type} className="checkbox-label">
                <input
                  type="checkbox"
                  checked={backupTypes[type]}
                  onChange={() => handleBackupTypeChange(type)}
                  disabled={loading}
                />
                <span>{type.charAt(0).toUpperCase() + type.slice(1)}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="backup-options">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={excludeUniqueId}
              onChange={(e) => setExcludeUniqueId(e.target.checked)}
              disabled={loading}
            />
            <span>Exclude unique_id data from exports</span>
          </label>
          <p className="text-muted" style={{ marginTop: '8px', fontSize: '0.9em' }}>
            When checked, the unique_id column will be kept but all values will be empty
          </p>
        </div>

        <button
          onClick={handleCreateBackup}
          className="btn btn-primary"
          disabled={loading || !selectedCemetery}
        >
          {loading ? 'Creating Backup...' : 'Create Backup'}
        </button>
      </div>

      {selectedCemetery && backupStatus && (
        <div className="card">
          <div className="card-header">
            <h2>Backup Progress</h2>
            {taskId && backupStatus.status === 'processing' && (
              <button
                onClick={handleCancelBackup}
                className="btn btn-danger btn-sm"
              >
                Cancel Backup
              </button>
            )}
          </div>
          <div className="backup-status">
            <div className="status-row">
              <span className="status-label">Status:</span>
              <span className={`status-value status-${backupStatus.status}`}>
                {backupStatus.status.toUpperCase()}
              </span>
            </div>
            {backupStatus.message && (
              <div className="status-row">
                <span className="status-label">Message:</span>
                <span className="status-value">{backupStatus.message}</span>
              </div>
            )}
            {backupStatus.progress && (
              <>
                <div className="status-row">
                  <span className="status-label">Current Step:</span>
                  <span className="status-value">{backupStatus.progress.current_step || 'Processing...'}</span>
                </div>
                <div className="status-row">
                  <span className="status-label">Completed Steps:</span>
                  <span className="status-value">
                    {backupStatus.progress.completed_steps || 0} / {backupStatus.progress.total_steps || '?'}
                  </span>
                </div>
                {backupStatus.progress.completed_steps && backupStatus.progress.total_steps && (
                  <div className="status-row">
                    <span className="status-label">Progress:</span>
                    <div className="progress-container">
                      <div className="progress-bar">
                        <div
                          className="progress-fill"
                          style={{
                            width: `${Math.round((backupStatus.progress.completed_steps / backupStatus.progress.total_steps) * 100)}%`
                          }}
                        ></div>
                      </div>
                      <span className="progress-text">
                        {Math.round((backupStatus.progress.completed_steps / backupStatus.progress.total_steps) * 100)}%
                      </span>
                    </div>
                  </div>
                )}
              </>
            )}
            {backupStatus.status === 'ready' && backupStatus.download_url && (
              <div className="status-row">
                <a
                  href={backupStatus.download_url}
                  className="btn btn-success btn-sm"
                  download
                >
                  Download Backup
                </a>
              </div>
            )}
            {backupStatus.error && (
              <div className="status-row error">
                <span className="status-label">Error:</span>
                <span className="status-value">{backupStatus.error}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {selectedCemetery && (
        <div className="card">
          <h2>Backup History</h2>
          <div className="backup-list">
            {backups.length === 0 ? (
              <div className="empty-state">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/>
                </svg>
                <p className="empty-title">No backups yet</p>
                <p className="empty-description">Create your first backup to get started</p>
              </div>
            ) : (
              backups.map((backup) => (
                <div key={backup.id} className="backup-item">
                  <div className="backup-info">
                    <div className="backup-header">
                      <h3>{backup.zip_filename || `Backup #${backup.id}`}</h3>
                      <span className={`backup-status-badge ${getStatusColor(backup.status)}`}>
                        {backup.status}
                      </span>
                    </div>
                    <p className="text-muted">
                      {backup.file_size_mb && `Size: ${backup.file_size_mb.toFixed(2)} MB | `}
                      Created: {new Date(backup.created_at).toLocaleString()}
                      {backup.completed_at && ` | Completed: ${new Date(backup.completed_at).toLocaleString()}`}
                    </p>
                    {backup.error_summary && (
                      <p className="text-error">{backup.error_summary}</p>
                    )}
                  </div>
                  <div className="backup-actions">
                    {backup.status === 'COMPLETED' && (
                      <>
                        <button
                          onClick={() => handleDownload(backup)}
                          className="btn btn-success btn-sm"
                        >
                          Download
                        </button>
                        <button
                          onClick={() => handleCompareToLive(backup)}
                          className="btn btn-primary btn-sm"
                        >
                          Compare to Live
                        </button>
                      </>
                    )}
                    {backup.status === 'FAILED' && (
                      <span className="text-muted">Failed</span>
                    )}
                    {['PENDING', 'PROCESSING'].includes(backup.status) && (
                      <span className="text-muted">In Progress...</span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default BackupPage;
